import { useState } from 'react';
import { Card } from '@/components/ui/card';
import type { Player, HitData } from '@/types/game';
import { teamColors } from '@/utils/game-utils';

interface HitMatrixProps {
  hitData: HitData[];
  players: Player[];
}

export const HitMatrix = ({ hitData, players }: HitMatrixProps) => {
  const [activeCol, setActiveCol] = useState<string | null>(null);

  if (!hitData || hitData.length === 0) return null;

  // Columns follow the exact same order as the rows so the self-diagonal lines up
  const orderedTargets = (() => {
    const remaining = [...players];
    const ordered: Player[] = [];
    hitData.forEach((row) => {
      const idx = remaining.findIndex((p) => p.name === row.player);
      if (idx !== -1) ordered.push(remaining.splice(idx, 1)[0]);
    });
    return [...ordered, ...remaining];
  })();

  // Heatmap scale: opacity = 0.18 + (value / max) * 0.82
  const maxHits = Math.max(
    1,
    ...hitData.flatMap((row) => Object.values(row.targets || {}))
  );

  const cellAlpha = (hits: number) => 0.18 + (hits / maxHits) * 0.82;

  // Short column labels for narrow screens (<640px): first 2 letters of the name,
  // extended per collision group (3, 4, ... letters) until unique; identical full
  // names get the vest number appended ("Chris·3"). Empty names fall back to "W<n>".
  const shortLabels = (() => {
    const labels = new Map<Player, string>();
    const trimmed = (p: Player) => p.name.trim();
    const fallback = (p: Player) => `W${p.weste}`;

    // Group players whose trimmed name is non-empty; empty ones get the vest fallback
    const named = orderedTargets.filter((p) => trimmed(p).length > 0);
    orderedTargets.forEach((p) => {
      if (trimmed(p).length === 0) labels.set(p, fallback(p));
    });

    // Iteratively lengthen only colliding groups
    let groups = new Map<string, Player[]>();
    named.forEach((p) => {
      const key = trimmed(p).slice(0, 2).toLowerCase();
      groups.set(key, [...(groups.get(key) || []), p]);
    });
    let length = 2;
    while (groups.size > 0) {
      const next = new Map<string, Player[]>();
      groups.forEach((players) => {
        if (players.length === 1) {
          labels.set(players[0], trimmed(players[0]).slice(0, length));
          return;
        }
        // Collision: lengthen only those names that can still grow
        const canGrow = players.filter((p) => trimmed(p).length > length);
        const stuck = players.filter((p) => trimmed(p).length <= length);
        if (canGrow.length === 0) {
          // Identical (or equally short) names — disambiguate by vest number
          stuck.forEach((p) => labels.set(p, `${trimmed(p)}·${p.weste}`));
          return;
        }
        // Short names that can't grow further keep their full name as label
        stuck.forEach((p) => labels.set(p, trimmed(p)));
        canGrow.forEach((p) => {
          const k = trimmed(p).slice(0, length + 1).toLowerCase();
          next.set(k, [...(next.get(k) || []), p]);
        });
      });
      groups = next;
      length += 1;
    }
    return labels;
  })();

  return (
    <Card className="p-3 rounded-2xl border border-border bg-card">
      <h2 className="text-lg font-bold">Treffermatrix</h2>
      <p className="text-xs text-muted-foreground mb-4">
        Zeile = Schütze · Spalte = getroffener Spieler
      </p>
      <div className="overflow-x-auto">
        <table
          className="w-full min-w-max"
          style={{ borderCollapse: 'separate', borderSpacing: '4px' }}
          onMouseLeave={() => setActiveCol(null)}
        >
          <caption className="sr-only">
            Treffermatrix: Zeilen zeigen den schießenden Spieler, Spalten den getroffenen Spieler.
          </caption>
          <thead>
            <tr>
              <th scope="col" className="p-1 text-left text-[11px] font-bold tracking-[0.12em] uppercase text-muted-foreground sticky left-0 z-10 bg-card">
                <span className="sr-only">Spieler</span>
              </th>
              {orderedTargets.map((player) => (
                <th
                  scope="col"
                  key={player.weste}
                  title={player.name}
                  className="p-1 text-center text-[11px] font-bold tracking-[0.12em] text-muted-foreground whitespace-nowrap"
                >
                  <span className="hidden sm:inline uppercase">{player.name}</span>
                  <span className="sm:hidden">{shortLabels.get(player)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orderedTargets.map((rowPlayer) => {
              const shooter = hitData.find((h) => h.player === rowPlayer.name) ?? { player: rowPlayer.name, targets: {} };
              const shooterColors = teamColors[rowPlayer.team as keyof typeof teamColors]
                || { bg: 'bg-muted/20', text: 'text-foreground', border: 'border-muted', glow: 'shadow-md' };

              return (
                <tr key={rowPlayer.weste}>
                  <th
                    scope="row"
                    className={`p-1 pr-2 text-left font-medium text-xs sm:text-sm ${shooterColors.text} sticky left-0 z-10 bg-card whitespace-nowrap`}
                  >
                    <span className="hidden sm:inline">{shooter.player}</span>
                    <span className="sm:hidden truncate block max-w-[80px]">{shooter.player.split(' ')[0]}</span>
                  </th>
                  {orderedTargets.map((target) => {
                    const isSelf = target.weste === rowPlayer.weste && target.name === rowPlayer.name;
                    const hits = isSelf ? 0 : (shooter.targets[target.weste] || 0);
                    const ratio = hits / maxHits;
                    const alpha = cellAlpha(hits);

                    if (isSelf) {
                      return (
                        <td
                          key={target.weste}
                          className="hit-matrix-diagonal h-[38px] min-w-[38px] rounded-[9px] text-center align-middle text-xs text-muted-foreground"
                        >
                          –
                        </td>
                      );
                    }

                    return (
                      <td
                        key={target.weste}
                        onMouseEnter={() => setActiveCol(target.weste)}
                        className={`h-[38px] min-w-[38px] rounded-[9px] text-center align-middle text-[13px] font-black ${
                          ratio > 0.55 ? 'text-foreground' : 'text-muted-foreground'
                        } ${activeCol === target.weste ? 'hit-matrix-col-active' : ''}`}
                        style={{ backgroundColor: `hsl(var(--primary) / ${alpha.toFixed(3)})` }}
                      >
                        {hits}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 mt-3 text-[11px] text-muted-foreground">
        <span>wenig</span>
        {[0.2, 0.4, 0.6, 0.8, 1.0].map((alpha) => (
          <span
            key={alpha}
            aria-hidden="true"
            className="inline-block w-[26px] h-[10px] rounded-[3px]"
            style={{ backgroundColor: `hsl(var(--primary) / ${alpha})` }}
          />
        ))}
        <span>viel</span>
      </div>
    </Card>
  );
};
